import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchPodSession,
  fetchOrganizationDetails,
  fetchAllPodAgentAlerts,
  fetchAllPodExamCodes,
  fetchPodNotesFormat,
  fetchOrCreateUser,
  updatePodSession as updatePodSessionApi,
  subscribeToPodSessionUpdates,
  claimPodSessionAndNotify,
  PodSessionAlreadyClaimedError,
} from "./api";
import { saveTesterJoined } from "./podQueueList";
import { getProctorUsername, isClaimableProctor } from "./sessionUtils";
import { mergeRemotePodSession } from "./notesMergeUtils";

export function usePodSessionData(sessionId, { onSaved, onClaimNotice } = {}) {
  const [podSession, setPodSession] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [podAgentAlerts, setPodAgentAlerts] = useState([]);
  const [podExamCodes, setPodExamCodes] = useState([]);
  const [podNotesFormat, setPodNotesFormat] = useState(null);
  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const onSavedRef = useRef(onSaved);
  const onClaimNoticeRef = useRef(onClaimNotice);
  const testerJoinedSaveRef = useRef(false);
  const localSaveUpdatedAtRef = useRef(null);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  useEffect(() => {
    onClaimNoticeRef.current = onClaimNotice;
  }, [onClaimNotice]);

  useEffect(() => {
    let cancelled = false;

    fetchOrCreateUser()
      .then(({ user: resolvedUser, email }) => {
        if (cancelled) return;
        setUser(resolvedUser);
        setUserEmail(email);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setPodSession(null);
      setOrganization(null);
      setPodAgentAlerts([]);
      setPodExamCodes([]);
      setPodNotesFormat(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function loadSessionData() {
      setLoading(true);
      setPodSession(null);
      setOrganization(null);
      setPodAgentAlerts([]);
      setPodExamCodes([]);
      setPodNotesFormat(null);

      try {
        const session = await fetchPodSession(sessionId);
        if (cancelled) return;

        if (!session) {
          setLoading(false);
          return;
        }

        setPodSession(session);

        if (!session.organizationId) {
          setLoading(false);
          return;
        }

        const [org, alerts, examCodes, notesFormat] = await Promise.all([
          fetchOrganizationDetails(session.organizationId),
          fetchAllPodAgentAlerts(session.organizationId),
          fetchAllPodExamCodes(session.organizationId),
          fetchPodNotesFormat(session.organizationId),
        ]);

        if (cancelled) return;

        setOrganization(org);
        setPodAgentAlerts(alerts);
        setPodExamCodes(examCodes);
        setPodNotesFormat(notesFormat);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSessionData();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return undefined;

    const subscription = subscribeToPodSessionUpdates(sessionId, {
      next: (remoteSession) => {
        // Skip the echo from our own save when updatedAt matches.
        if (
          remoteSession.updatedAt &&
          remoteSession.updatedAt === localSaveUpdatedAtRef.current
        ) {
          return;
        }

        setPodSession((prev) => mergeRemotePodSession(prev, remoteSession));
      },
    });

    return () => subscription.unsubscribe();
  }, [sessionId]);

  const updatePodSession = useCallback(
    async (input, { showToast = true } = {}) => {
      if (!sessionId) return null;

      const updated = await updatePodSessionApi(
        { id: sessionId, ...input },
        "AMAZON_COGNITO_USER_POOLS",
      );

      if (updated?.updatedAt) {
        localSaveUpdatedAtRef.current = updated.updatedAt;
      }

      setPodSession((prev) =>
        prev ? { ...prev, ...(updated ?? {}), ...input } : updated,
      );

      if (showToast) {
        onSavedRef.current?.();
      }

      return updated;
    },
    [sessionId],
  );

  const claimSession = useCallback(async () => {
    if (!sessionId || !userEmail || !isClaimableProctor(podSession?.proctor)) {
      return;
    }

    const proctor = getProctorUsername(user, userEmail);
    setClaiming(true);

    try {
      const { session, notificationError } = await claimPodSessionAndNotify(
        sessionId,
        proctor,
        { expectedProctor: podSession?.proctor },
      );

      if (session?.updatedAt) {
        localSaveUpdatedAtRef.current = session.updatedAt;
      }

      setPodSession((prev) =>
        prev ? { ...prev, ...(session ?? {}), proctor } : session,
      );

      if (notificationError) {
        onClaimNoticeRef.current?.(
          "Session claimed, but other proctors were not notified.",
        );
      }
    } catch (error) {
      if (error instanceof PodSessionAlreadyClaimedError) {
        const winner = error.session?.proctor || "another proctor";
        if (error.session) {
          setPodSession((prev) =>
            prev ? { ...prev, ...error.session } : error.session,
          );
        }
        onClaimNoticeRef.current?.(`Claimed by ${winner}`);
        return;
      }
      console.error(error);
    } finally {
      setClaiming(false);
    }
  }, [podSession?.proctor, sessionId, user, userEmail]);

  const setTesterHasJoined = useCallback(
    async (next) => {
      if (!sessionId || testerJoinedSaveRef.current) return null;

      const previous = podSession?.hasTesterJoined;
      testerJoinedSaveRef.current = true;
      setPodSession((prev) =>
        prev ? { ...prev, hasTesterJoined: next } : prev,
      );

      const result = await saveTesterJoined({
        previous,
        next,
        save: (value) =>
          updatePodSession({ hasTesterJoined: value }, { showToast: false }),
      });

      testerJoinedSaveRef.current = false;

      if (result.error) {
        setPodSession((prev) =>
          prev ? { ...prev, hasTesterJoined: result.value } : prev,
        );
        onClaimNoticeRef.current?.("Unable to save tester joined status.");
      } else if (result.historyError) {
        onClaimNoticeRef.current?.(
          "Tester joined was saved, but the history record failed.",
        );
      }

      return result;
    },
    [podSession?.hasTesterJoined, sessionId, updatePodSession],
  );

  const unclaimSession = useCallback(async () => {
    if (!sessionId || !podSession?.proctor) return;

    setClaiming(true);

    try {
      await updatePodSession({ proctor: null });
    } catch (error) {
      console.error(error);
    } finally {
      setClaiming(false);
    }
  }, [podSession?.proctor, sessionId, updatePodSession]);

  return {
    podSession,
    organization,
    podAgentAlerts,
    podExamCodes,
    podNotesFormat,
    user,
    setUser,
    userEmail,
    loading,
    claiming,
    claimSession,
    unclaimSession,
    updatePodSession,
    setTesterHasJoined,
  };
}
